/* ── Managed content: the match facts and Kourt copy edited at /admin ────
 *
 * The Team and Kourt pages used to carry the week's lineup, venue, forecast,
 * schedule and prose as constants edited by hand every week. They now live
 * as JSON — published from /admin into the `tennis` blob container and served
 * by GET /api/content/<kind> — so the website and the iOS app read one copy.
 * `public/content/<kind>.json` is the seed: the server serves it until the
 * first publish, and the browser falls back to it when the API is down.
 *
 * Everything that arrives is coerced through the sanitizers below; nothing
 * from storage is trusted as-is. The server keeps a JS twin of the same rules
 * (sanitizeTeamContent / sanitizeKourtContent in server.js) — keep them in step.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import type { CaseKind } from './kourt';

/* ── Team ──────────────────────────────────────────────────────────────── */

export interface Player {
  name: string;
  /** Cookout duty, when assigned. ALTA's "Bring" field. */
  bring?: string;
}

export interface Line {
  id: string;
  /** Null until the captain publishes the week's lineup on the ALTA site. */
  players: [Player, Player] | null;
  /** When this court goes on, from ALTA's Match Time column. */
  time?: string;
}

export interface SeasonInfo {
  name: string;
  club: string;
  rosterId: string;
  division: string;
  coordinator: string;
  captain: string;
  coCaptain: string;
}

export interface Venue {
  name: string;
  address: string;
  courts: string;
  /** For the forecast fetch; optional so an away venue can be typed in fast. */
  lat?: number;
  lon?: number;
  googleMaps: string;
  appleMaps: string;
}

export interface WeatherHour {
  time: string;
  temp: number;
  pop: number;
  storm: boolean;
  note?: string;
}

export interface Weather {
  pulledOn: string;
  pulledAt: string;
  place: string;
  kicker: string;
  icon: string;
  headline: string;
  /** Mini-markup; `{chance}`, `{firstServe}`, `{noon}`, `{high}`, `{low}` are substituted. */
  lede: string;
  /** Mini-markup; `{pulledOn}` and `{place}` are substituted. */
  foot: string;
  chance: number;
  laterChance: number;
  high: number;
  low: number;
  firstServe: number;
  noon: number;
  hours: WeatherHour[];
}

export interface LineScore {
  id: string;
  players: string;
  /** Games by set, ours first: "6-3 6-7 10-6". */
  sets: string;
  won: boolean;
}

export interface MatchResult {
  us: number;
  them: number;
  note?: string;
  lines: LineScore[];
}

export interface Match {
  week: number;
  /** ISO day, used for ordering and for picking the next match up. */
  iso: string;
  label: string;
  /** True when we travel; false means we host. */
  away?: boolean;
  opponent?: string;
  opponentCaptain?: string;
  bye?: boolean;
  /** Filled in once the match is over; its presence is what makes it "played". */
  result?: MatchResult;
}

export interface Notice {
  id: string;
  /** Picks the styling: 'mojito' on the Next Up card, 'cookout' as a page alert. */
  style: string;
  icon: string;
  /** Mini-markup. */
  text: string;
}

export interface TeamContent {
  version: 1;
  updatedAt: string;
  updatedBy: string;
  /** Which match the scoreboard blob belongs to; bumping it blanks the board. */
  seasonKey: string;
  setsPerLine: number;
  season: SeasonInfo;
  lineup: Line[];
  venue: Venue;
  weather: Weather;
  schedule: Match[];
  header: { tag: string; paragraph: string; kourtTease: string };
  nextUp: {
    dateLine: string;
    bannerSoon: string;
    bannerFar: string;
    seasonDone: string;
    lineupNote: string;
    notices: Notice[];
  };
  notices: Notice[];
  scheduleFoot: string;
  charges: string[];
}

/* ── Kourt ─────────────────────────────────────────────────────────────── */

export interface Guideline {
  label: string;
  kind: CaseKind;
  amount: number;
  offense: string;
}

export interface KourtContent {
  version: 1;
  updatedAt: string;
  updatedBy: string;
  judge: {
    name: string;
    rosterName: string;
    presiding: string;
    paw: string;
    tagline: string;
    /** Site-relative image path; must start with /images/. */
    portrait: string;
    portraitAlt: string;
  };
  kicker: string;
  /** Mini-markup. */
  lede: string;
  rules: string[];
  /** The most a complaint may ask for; only the Bench goes higher. */
  complaintMax: number;
  guidelines: Guideline[];
  openers: string[];
}

/* ── Roster (private: names, roles, emails, mobiles — admin only) ──────── */

export interface RosterPlayer {
  name: string;
  email: string;
  /** E.164, US only, or ''. */
  phone: string;
  role: '' | 'Captain' | 'Co-Captain';
  notes: string;
  /** One of the Thursday practice group: gets the Fireball tab in the app. */
  fireball: boolean;
  /** Other addresses he signs in with (e.g. a Gmail for the Android app); notices still go to `email`. */
  altEmails: string[];
}

export interface RosterContent {
  version: 1;
  updatedAt: string;
  updatedBy: string;
  players: RosterPlayer[];
}

/* ── Sanitizers ────────────────────────────────────────────────────────── */

type Raw = Record<string, unknown>;

const isObj = (v: unknown): v is Raw => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, max: number, fallback = ''): string =>
  typeof v === 'string' ? v.slice(0, max) : fallback;
const int = (v: unknown, min: number, max: number, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : fallback;
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const bool = (v: unknown, fallback = false): boolean => (typeof v === 'boolean' ? v : fallback);
const list = <T>(v: unknown, max: number, each: (item: unknown) => T | null): T[] =>
  (Array.isArray(v) ? v.slice(0, max) : []).map(each).filter((x): x is T => x !== null);

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const SETS = /^\d{1,2}-\d{1,2}( \d{1,2}-\d{1,2}){0,2}$/;
const LINE_ID = /^[A-Za-z0-9]{1,4}$/;
const IMAGE_PATH = /^\/images\/[A-Za-z0-9._-]{1,80}\.(jpe?g|png|webp)$/i;
/** Only the two map hosts, so managed content can never link somewhere odd. */
const MAP_LINK = /^https:\/\/(www\.google\.com\/maps\/|maps\.apple\.com\/)/;

export const CONTENT_LIMITS = {
  lineup: 8,
  schedule: 20,
  hours: 12,
  notices: 6,
  charges: 20,
  rules: 12,
  guidelines: 24,
  openers: 12,
  paragraph: 1200,
  line: 300,
  short: 80,
} as const;

function toPlayer(raw: unknown): Player | null {
  if (!isObj(raw)) return null;
  const name = str(raw.name, 60).trim();
  if (!name) return null;
  const bring = str(raw.bring, 80).trim();
  return bring ? { name, bring } : { name };
}

function toLine(raw: unknown): Line | null {
  if (!isObj(raw)) return null;
  const id = str(raw.id, 4).trim();
  if (!LINE_ID.test(id)) return null;
  const pair = Array.isArray(raw.players) ? raw.players.slice(0, 2).map(toPlayer) : [];
  const players: [Player, Player] | null =
    pair.length === 2 && pair[0] && pair[1] ? [pair[0], pair[1]] : null;
  const time = str(raw.time, 20).trim();
  return time ? { id, players, time } : { id, players };
}

function toHour(raw: unknown): WeatherHour | null {
  if (!isObj(raw)) return null;
  const time = str(raw.time, 12).trim();
  if (!time) return null;
  const note = str(raw.note, 40).trim();
  return {
    time,
    temp: int(raw.temp, -40, 130, 0),
    pop: int(raw.pop, 0, 100, 0),
    storm: bool(raw.storm),
    ...(note ? { note } : {}),
  };
}

function toLineScore(raw: unknown): LineScore | null {
  if (!isObj(raw)) return null;
  const id = str(raw.id, 4).trim();
  const sets = str(raw.sets, 30).trim();
  if (!LINE_ID.test(id) || !SETS.test(sets)) return null;
  return { id, players: str(raw.players, 120).trim(), sets, won: bool(raw.won) };
}

function toResult(raw: unknown): MatchResult | undefined {
  if (!isObj(raw)) return undefined;
  const note = str(raw.note, CONTENT_LIMITS.paragraph).trim();
  return {
    us: int(raw.us, 0, 20, 0),
    them: int(raw.them, 0, 20, 0),
    ...(note ? { note } : {}),
    lines: list(raw.lines, CONTENT_LIMITS.lineup, toLineScore),
  };
}

function toMatch(raw: unknown): Match | null {
  if (!isObj(raw)) return null;
  const iso = str(raw.iso, 10);
  if (!ISO_DAY.test(iso)) return null;
  const week = int(raw.week, 1, 30, 0);
  if (!week) return null;
  const match: Match = { week, iso, label: str(raw.label, 40).trim() || iso };
  if (bool(raw.bye)) {
    match.bye = true;
    return match;
  }
  match.away = bool(raw.away);
  const opponent = str(raw.opponent, 80).trim();
  if (opponent) match.opponent = opponent;
  const captain = str(raw.opponentCaptain, 60).trim();
  if (captain) match.opponentCaptain = captain;
  const result = toResult(raw.result);
  if (result) match.result = result;
  return match;
}

function toNotice(raw: unknown): Notice | null {
  if (!isObj(raw)) return null;
  const text = str(raw.text, CONTENT_LIMITS.paragraph).trim();
  if (!text) return null;
  return {
    id: str(raw.id, 24).trim() || 'notice',
    style: str(raw.style, 24).trim().replace(/[^a-z0-9-]/gi, '') || 'plain',
    icon: str(raw.icon, 8),
    text,
  };
}

function toStrings(raw: unknown, max: number, each: number): string[] {
  return list(raw, max, (v) => {
    const s = str(v, each).trim();
    return s || null;
  });
}

/** Coerce anything that claims to be team content; `null` when it is not even close. */
export function sanitizeTeamContent(raw: unknown): TeamContent | null {
  if (!isObj(raw)) return null;
  const season = isObj(raw.season) ? raw.season : {};
  const venue = isObj(raw.venue) ? raw.venue : {};
  const weather = isObj(raw.weather) ? raw.weather : {};
  const header = isObj(raw.header) ? raw.header : {};
  const nextUp = isObj(raw.nextUp) ? raw.nextUp : {};
  const seasonKey = str(raw.seasonKey, 40).trim();
  const lineup = list(raw.lineup, CONTENT_LIMITS.lineup, toLine);
  if (!seasonKey || lineup.length === 0) return null;
  const lat = num(venue.lat);
  const lon = num(venue.lon);
  const S = CONTENT_LIMITS.short;
  const P = CONTENT_LIMITS.paragraph;
  return {
    version: 1,
    updatedAt: str(raw.updatedAt, 40),
    updatedBy: str(raw.updatedBy, 80),
    seasonKey,
    setsPerLine: int(raw.setsPerLine, 1, 3, 3),
    season: {
      name: str(season.name, S),
      club: str(season.club, S),
      rosterId: str(season.rosterId, 20),
      division: str(season.division, 20),
      coordinator: str(season.coordinator, 60),
      captain: str(season.captain, 60),
      coCaptain: str(season.coCaptain, 60),
    },
    lineup,
    venue: {
      name: str(venue.name, 120),
      address: str(venue.address, 160),
      courts: str(venue.courts, 120),
      ...(lat !== undefined && lon !== undefined ? { lat, lon } : {}),
      googleMaps: MAP_LINK.test(str(venue.googleMaps, 400)) ? str(venue.googleMaps, 400) : '',
      appleMaps: MAP_LINK.test(str(venue.appleMaps, 400)) ? str(venue.appleMaps, 400) : '',
    },
    weather: {
      pulledOn: str(weather.pulledOn, 60),
      pulledAt: str(weather.pulledAt, 40),
      place: str(weather.place, 60),
      kicker: str(weather.kicker, 120),
      icon: str(weather.icon, 8),
      headline: str(weather.headline, 120),
      lede: str(weather.lede, P),
      foot: str(weather.foot, P),
      chance: int(weather.chance, 0, 100, 0),
      laterChance: int(weather.laterChance, 0, 100, 0),
      high: int(weather.high, -40, 130, 0),
      low: int(weather.low, -40, 130, 0),
      firstServe: int(weather.firstServe, -40, 130, 0),
      noon: int(weather.noon, -40, 130, 0),
      hours: list(weather.hours, CONTENT_LIMITS.hours, toHour),
    },
    schedule: list(raw.schedule, CONTENT_LIMITS.schedule, toMatch).sort((a, b) => a.iso.localeCompare(b.iso)),
    header: {
      tag: str(header.tag, S),
      paragraph: str(header.paragraph, P),
      kourtTease: str(header.kourtTease, P),
    },
    nextUp: {
      dateLine: str(nextUp.dateLine, S),
      bannerSoon: str(nextUp.bannerSoon, P),
      bannerFar: str(nextUp.bannerFar, P),
      seasonDone: str(nextUp.seasonDone, P),
      lineupNote: str(nextUp.lineupNote, P),
      notices: list(nextUp.notices, CONTENT_LIMITS.notices, toNotice),
    },
    notices: list(raw.notices, CONTENT_LIMITS.notices, toNotice),
    scheduleFoot: str(raw.scheduleFoot, P),
    charges: toStrings(raw.charges, CONTENT_LIMITS.charges, S),
  };
}

function toGuideline(raw: unknown): Guideline | null {
  if (!isObj(raw)) return null;
  const label = str(raw.label, 40).trim();
  const offense = str(raw.offense, 300).trim();
  if (!label || !offense) return null;
  return {
    label,
    kind: raw.kind === 'bonus' ? 'bonus' : 'fine',
    amount: int(raw.amount, 0, 500, 1),
    offense,
  };
}

export function sanitizeKourtContent(raw: unknown): KourtContent | null {
  if (!isObj(raw)) return null;
  const judge = isObj(raw.judge) ? raw.judge : {};
  const name = str(judge.name, 80).trim();
  if (!name) return null;
  const portrait = str(judge.portrait, 120);
  return {
    version: 1,
    updatedAt: str(raw.updatedAt, 40),
    updatedBy: str(raw.updatedBy, 80),
    judge: {
      name,
      rosterName: str(judge.rosterName, 60).trim(),
      presiding: str(judge.presiding, 40),
      paw: str(judge.paw, 40),
      tagline: str(judge.tagline, 120),
      portrait: IMAGE_PATH.test(portrait) ? portrait : '',
      portraitAlt: str(judge.portraitAlt, 300),
    },
    kicker: str(raw.kicker, 120),
    lede: str(raw.lede, CONTENT_LIMITS.paragraph),
    rules: toStrings(raw.rules, CONTENT_LIMITS.rules, CONTENT_LIMITS.line),
    complaintMax: int(raw.complaintMax, 1, 500, 25),
    guidelines: list(raw.guidelines, CONTENT_LIMITS.guidelines, toGuideline),
    openers: toStrings(raw.openers, CONTENT_LIMITS.openers, 200),
  };
}

/** E.164, US only; '' when it is not a number. Twin of the server's. */
export function normalizePhone(value: unknown): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return /^[2-9]\d{9}$/.test(ten) ? `+1${ten}` : '';
}

function toRosterPlayer(raw: unknown): RosterPlayer | null {
  if (!isObj(raw)) return null;
  const name = str(raw.name, 60).trim();
  if (!name) return null;
  const email = str(raw.email, 120).trim().toLowerCase();
  return {
    name,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '',
    phone: normalizePhone(raw.phone),
    role: raw.role === 'Captain' || raw.role === 'Co-Captain' ? raw.role : '',
    notes: str(raw.notes, 200).trim(),
    fireball: bool(raw.fireball),
    altEmails: (Array.isArray(raw.altEmails) ? raw.altEmails : String(raw.altEmails ?? '').split(/[\s,;]+/))
      .map((v) => str(v, 120).trim().toLowerCase())
      .filter((v, i, all) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && all.indexOf(v) === i)
      .slice(0, 5),
  };
}

export function sanitizeRosterContent(raw: unknown): RosterContent | null {
  if (!isObj(raw)) return null;
  const seen = new Set<string>();
  const players = list(raw.players, 60, toRosterPlayer).filter((p) => {
    const key = p.name.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return {
    version: 1,
    updatedAt: str(raw.updatedAt, 40),
    updatedBy: str(raw.updatedBy, 80),
    players: players.sort((a, b) => a.name.localeCompare(b.name)),
  };
}

/** The private roster, for the admin only: needs the passcode as a bearer. */
export async function fetchRoster(passcode: string): Promise<{ content: RosterContent | null; status: number }> {
  try {
    const response = await fetch('/api/content/roster', { cache: 'no-store', headers: { Authorization: `Bearer ${passcode}` } });
    if (!response.ok) return { content: null, status: response.status };
    return { content: sanitizeRosterContent(await response.json()), status: 200 };
  } catch {
    return { content: null, status: 0 };
  }
}

/* ── Loading ───────────────────────────────────────────────────────────── */

export type ContentKind = 'team' | 'kourt';

/** The API first (what /admin last published), the shipped seed if it is down. */
export async function fetchContent(kind: 'team'): Promise<{ content: TeamContent | null; source: 'live' | 'static' }>;
export async function fetchContent(kind: 'kourt'): Promise<{ content: KourtContent | null; source: 'live' | 'static' }>;
export async function fetchContent(kind: ContentKind): Promise<{ content: TeamContent | KourtContent | null; source: 'live' | 'static' }> {
  const sanitize = kind === 'team' ? sanitizeTeamContent : sanitizeKourtContent;
  for (const [url, source] of [
    [`/api/content/${kind}`, 'live'],
    [`/content/${kind}.json`, 'static'],
  ] as const) {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) continue;
      const content = sanitize(await response.json());
      if (content) return { content, source };
    } catch {
      /* try the next source */
    }
  }
  return { content: null, source: 'static' };
}

/** /admin renders the real pages with an unpublished draft by wrapping them in this. */
export const ContentOverride = createContext<{ team?: TeamContent; kourt?: KourtContent }>({});

export interface Loaded<T> {
  /** Null until the first fetch settles (either source). */
  content: T | null;
  source: 'live' | 'static' | 'draft';
  /** Fetch again — the pages call this when the relay says `content` changed. */
  reload: () => void;
}

function useContent<T extends TeamContent | KourtContent>(kind: ContentKind, override: T | undefined): Loaded<T> {
  const [state, setState] = useState<{ content: T | null; source: 'live' | 'static' }>({
    content: null,
    source: 'static',
  });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    if (override) return;
    let cancelled = false;
    void (kind === 'team' ? fetchContent('team') : fetchContent('kourt')).then((result) => {
      if (!cancelled) setState(result as { content: T | null; source: 'live' | 'static' });
    });
    return () => {
      cancelled = true;
    };
  }, [kind, tick, override]);
  if (override) return { content: override, source: 'draft', reload };
  return { ...state, reload };
}

export function useTeamContent(): Loaded<TeamContent> {
  return useContent<TeamContent>('team', useContext(ContentOverride).team);
}

export function useKourtContent(): Loaded<KourtContent> {
  return useContent<KourtContent>('kourt', useContext(ContentOverride).kourt);
}
